import { useQuery, useQueryClient } from "@tanstack/react-query";
import { queryKeys } from "./queryKeys";
import { chatFetchSearchUserApi } from "../services/apiService";
import { getUnreadCounts } from "../features/doctor-messages/doctorMessages.utils";
import { useBroadcastListener } from "../services/broadcast/broadcastService";

// Same endpoint the Messages page uses to build its own doctor/patient tab
// counters (chat/fetch-user already returns unread_messages per conversation).
// Polled on an interval since new messages aren't broadcast in real time yet.
const UNREAD_MESSAGES_REFETCH_INTERVAL = 30 * 1000; // 30 seconds

/**
 * Total unread message count for the sidebar "Messages" badge.
 * @param {string|number} userId doctor id — only used to scope the cache key.
 */
export function useUnreadMessagesCount(userId, options = {}) {
  return useQuery({
    queryKey: queryKeys.messages.unreadCount(userId),
    queryFn: async () => {
      const formData = new FormData();
      formData.append("search_name", "");
      const response = await chatFetchSearchUserApi(formData);
      if (response?.data?.res !== true) return 0;

      const { doctorCount, patientCount } = getUnreadCounts(
        response.data.doctor ?? [],
        response.data.patients ?? [],
      );
      return doctorCount + patientCount;
    },
    enabled: Boolean(userId),
    staleTime: 15 * 1000,
    refetchInterval: UNREAD_MESSAGES_REFETCH_INTERVAL,
    refetchOnWindowFocus: true,
    ...options,
  });
}

/** Force an immediate refresh — call after leaving the Messages page. */
export function useRefreshUnreadMessagesCount(userId) {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: queryKeys.messages.unreadCount(userId) });
}

/**
 * Real-time sync for the sidebar "Messages" badge: the backend broadcasts
 * `message.received` on the shared `doctor.{id}.channel` (same channel the
 * waiting room uses) whenever a message is sent to this doctor — see
 * NewMessageReceived / DoctorService::sendMessage. Refetching the unread
 * count right away means the badge appears the instant the message arrives,
 * instead of waiting for the 30s poll.
 */
export function useUnreadMessagesRealtimeSync(userId) {
  const queryClient = useQueryClient();

  useBroadcastListener({
    channelName: userId ? `doctor.${userId}.channel` : null,
    isPrivate: true,
    enabled: Boolean(userId),
    events: {
      ".message.received": () =>
        queryClient.invalidateQueries({ queryKey: queryKeys.messages.unreadCount(userId) }),
    },
  });
}
